import { StorageInterface } from '../stores/childNavStores';
import type { ContentRecord } from '../stores/contentStore';
import { ConfigManager } from '../config/configManager';

/**
 * Represents a single stored base64 image record.
 * Mirrors the C# Base64ImageDto (PascalCase on the wire, camelCase stored locally).
 *
 * Wire format (inside content_body, base64-encoded JSON):
 *   { "ImageId": "3", "DName": "App Logo", "Base64": "<img src=\"data:image/png;base64,...\" />" }
 */
export interface Base64ImageDto {
  /** Unique identifier matching the C# ImageId (e.g. "3") */
  imageId: string;
  /** Display name from C# DName */
  dName: string;
  /** Full HTML <img> tag whose src attribute contains the data-URI (data:image/...;base64,...) */
  base64: string;
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

/**
 * Extract the src data-URI from the HTML <img> tag stored in Base64ImageDto.base64.
 * Returns null when the src cannot be found.
 *
 * Example input:  '<img src="data:image/png;base64,iVBOR..." />'
 * Example output: 'data:image/png;base64,iVBOR...'
 */
export const extractImgSrc = (imgTag: string): string | null => {
  const match = /src=["']([^"']+)["']/.exec(imgTag);
  return match ? match[1] : null;
};

/**
 * Base64ImageStorage — manages local storage of base64 image records.
 *
 * Storage Structure:
 *   Key:   b64img_{imageId}
 *   Value: JSON-serialised Base64ImageDto
 *
 * Server content_body format (base64-encoded JSON, PascalCase):
 * {
 *   "ImageId": "3",
 *   "DName":   "App Logo",
 *   "Base64":  "<img src=\"data:image/png;base64,...\" />"
 * }
 */
export class Base64ImageStorage {
  private storage: StorageInterface;
  private readonly keyPrefix = 'b64img_';
  private readonly indexKey = 'b64img_index';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private storageKey(imageId: string): string {
    return `${this.keyPrefix}${imageId}`;
  }

  private decodeBase64(b64: string): string {
    try {
      if (typeof Buffer !== 'undefined') {
        return Buffer.from(b64, 'base64').toString('utf-8');
      }
      return atob(b64);
    } catch (err) {
      throw new Error(`Base64ImageStorage: failed to decode base64 string — ${err}`);
    }
  }

  /** Maintain a lightweight index of all stored imageIds for fast getAllImages(). */
  private async addToIndex(imageId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(imageId)) {
        ids.push(imageId);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch (_) { /* non-fatal */ }
  }

  private async removeFromIndex(imageId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const updated = ids.filter(id => id !== imageId);
      await this.storage.setItem(this.indexKey, JSON.stringify(updated));
    } catch (_) { /* non-fatal */ }
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Persist a Base64ImageDto.
   * Returns true on success.
   */
  async saveImage(dto: Base64ImageDto): Promise<boolean> {
    try {
      console.log('💾 Base64ImageStorage: saving image', dto.imageId);
      const record: Base64ImageDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      await this.storage.setItem(this.storageKey(dto.imageId), JSON.stringify(record));
      await this.addToIndex(dto.imageId);
      console.log('✅ Base64ImageStorage: saved', dto.imageId);
      // Notify any UI listeners (e.g. HomeView) so they can reload the logo
      ConfigManager.notifyLogoImageUpdated();
      return true;
    } catch (err) {
      console.error('❌ Base64ImageStorage: saveImage failed', err);
      return false;
    }
  }

  /**
   * Retrieve a single image by id. Returns null when not found.
   */
  async getImage(imageId: string): Promise<Base64ImageDto | null> {
    try {
      const raw = await this.storage.getItem(this.storageKey(imageId));
      return raw ? (JSON.parse(raw) as Base64ImageDto) : null;
    } catch (err) {
      console.error('❌ Base64ImageStorage: getImage failed', imageId, err);
      return null;
    }
  }

  /**
   * Return all stored images (reads index then fetches each record).
   */
  async getAllImages(): Promise<Base64ImageDto[]> {
    const ids = await this.getIndex();
    const results: Base64ImageDto[] = [];
    for (const id of ids) {
      const img = await this.getImage(id);
      if (img) results.push(img);
    }
    return results;
  }

  /**
   * Delete a single image by id.
   */
  async deleteImage(imageId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.storageKey(imageId));
      await this.removeFromIndex(imageId);
      console.log('🗑️ Base64ImageStorage: deleted', imageId);
      return true;
    } catch (err) {
      console.error('❌ Base64ImageStorage: deleteImage failed', imageId, err);
      return false;
    }
  }

  /**
   * Remove all stored images and the index.
   */
  async clearAll(): Promise<void> {
    const ids = await this.getIndex();
    for (const id of ids) {
      try {
        await this.storage.removeItem(this.storageKey(id));
      } catch (_) { /* continue */ }
    }
    try {
      await this.storage.removeItem(this.indexKey);
    } catch (_) { /* non-fatal */ }
    console.log('🗑️ Base64ImageStorage: cleared all images');
  }

  // ─── Content-record unpacking ─────────────────────────────────────────────

  /**
   * Unpack a ContentStore record of type "Base64Image" and persist it.
   *
   * Expected record.tableData shape (standard content-server response):
   *   Columns: [..., "content_body", ...]
   *   Rows:    [[..., "<base64-encoded JSON>", ...]]
   *
   * The base64-encoded JSON payload must match:
   *   { ImageId, DName, Base64 }
   *
   * Returns the stored dto on success, null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<Base64ImageDto | null> {
    try {
      console.log('\n🔍 Base64ImageStorage.unpackFromContentRecord called for:', record.id);

      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ Base64ImageStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');
      if (bodyIdx === -1) {
        console.warn('⚠️ Base64ImageStorage: content_body column not found. Available:', columns);
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyIdx];
      if (!encodedBody) {
        console.warn('⚠️ Base64ImageStorage: content_body is empty');
        return null;
      }

      console.log('📦 Base64ImageStorage: decoding content_body, length:', encodedBody.length);
      const jsonString = this.decodeBase64(encodedBody);
      const parsed = JSON.parse(jsonString);

      // Accept both PascalCase (from C# backend) and camelCase
      const dto: Base64ImageDto = {
        imageId:      parsed.ImageId  ?? parsed.imageId,
        dName:        parsed.DName    ?? parsed.dName    ?? '',
        base64:       parsed.Base64   ?? parsed.base64   ?? '',
        downloadedAt: new Date().toISOString(),
      };

      if (!dto.imageId || !dto.base64) {
        console.warn('⚠️ Base64ImageStorage: parsed dto missing imageId or base64:', dto);
        return null;
      }

      const saved = await this.saveImage(dto);
      if (!saved) return null;

      console.log(`✅ Base64ImageStorage: unpacked & stored image "${dto.imageId}" (${dto.dName})`);  
      return dto;
    } catch (err) {
      console.error('❌ Base64ImageStorage.unpackFromContentRecord failed:', err);
      return null;
    }
  }

  /**
   * Convenience overload — accept a raw tableData object directly (for bundle loading
   * where the ContentRecord wrapper may not be fully formed).
   */
  async unpackFromTableData(
    imageId: string,
    tableData: any
  ): Promise<Base64ImageDto | null> {
    const syntheticRecord = { id: `Base64Image_${imageId}_1`, tableData } as unknown as ContentRecord;
    return this.unpackFromContentRecord(syntheticRecord);
  }
}
